import React, { useRef, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { setDoc, doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import styled from 'styled-components';
import { useAuth } from './contexts/AuthContext'; // Importujte kontext pro autentizaci

const ZmenaPrezdivky = ({ closeModal }) => {
    const { user, updateUserDisplayName } = useAuth(); // Získání uživatelského objektu
    const inputRef = useRef(null);
    const [timeLeft, setTimeLeft] = useState(null);
    const [serverError, setServerError] = useState('');

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, []);

    useEffect(() => {
        let interval;
        if (timeLeft !== null) {
            interval = setInterval(() => {
                setTimeLeft((prevTimeLeft) => {
                    const remainingTime = Math.max(0, prevTimeLeft - 1000);
                    return remainingTime;
                });
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [timeLeft]);

    const formatTime = (time) => {
        const totalSeconds = Math.floor(time / 1000);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${minutes} minut a ${seconds} sekund`;
    };

    const formik = useFormik({
        initialValues: {
            nickname: ''
        },
        validationSchema: Yup.object({
            nickname: Yup.string()
                .required('Přezdívka je povinná')
                .min(5, 'Přezdívka musí mít alespoň 5 znaků')
                .max(15, 'Přezdívka nesmí mít více než 15 znaků')
        }),
        onSubmit: async (values) => {
            if (!user) {
                console.error('Není přihlášen žádný uživatel.');
                return;
            }

            try {
                const userRef = doc(db, 'users', user.uid);
                const userDoc = await getDoc(userRef);
                const lastChange = userDoc.data()?.lastNicknameChange?.toDate();
                const now = new Date();

                if (lastChange && (now - lastChange) < 3600000) { // 3600000 ms = 1 hodina
                    setTimeLeft(3600000 - (now - lastChange));
                    setServerError('Přezdívku můžete měnit pouze jednou za hodinu.');
                    return;
                }

                // Aktualizace přezdívky v Firestore
                await setDoc(userRef, { displayName: values.nickname, lastNicknameChange: now }, { merge: true });
                
                // Aktualizace přezdívky v AuthContext
                await updateUserDisplayName(values.nickname);

                console.log('Přezdívka úspěšně aktualizována');
                closeModal();
            } catch (error) {
                console.error('Chyba při aktualizaci přezdívky:', error);
                setServerError('Došlo k chybě při aktualizaci přezdívky.');
            }
        }
    });

    return (
        <ModalContent>
            <ModalTitle>Změna přezdívky</ModalTitle>
            <form onSubmit={formik.handleSubmit}>
                <ModalInput 
                    type="text"
                    {...formik.getFieldProps('nickname')}
                    placeholder="Zadejte novou přezdívku"
                    ref={inputRef}
                    autoComplete="off"
                    autoCorrect="off"
                />
                {formik.touched.nickname && formik.errors.nickname ? (
                    <ErrorText>{formik.errors.nickname}</ErrorText>
                ) : null}
                {serverError && (
                    <ErrorText>
                        {serverError}
                        {timeLeft !== null && timeLeft > 0 && (
                            <TimeLeftText> Můžete změnit za {formatTime(timeLeft)}.</TimeLeftText>
                        )}
                    </ErrorText>
                )}
                <ModalButtons>
                    <ModalButton type="submit">Uložit</ModalButton>
                    <ModalButton type="button" onClick={closeModal}>Zavřít</ModalButton>
                </ModalButtons>
            </form>
        </ModalContent>
    );
};

// Stylované komponenty
const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    width: 520px;
    max-width: calc(100vw - 48px);
    padding: 18px 20px;
    border-radius: 12px;
    box-sizing: border-box;
    background: #071014;
    color: #fff;
    border: 1px solid rgba(255,255,255,0.04);
    box-shadow: 0 20px 60px rgba(0,0,0,0.6);
`;

const ModalTitle = styled.h2`
    margin: 6px 0 12px 0;
    font-size: 20px;
    text-align: left;
    font-weight: 700;
`;

const ModalInput = styled.input`
    margin-bottom: 8px;
    padding: 12px;
    border-radius: 8px;
    border: 1px solid rgba(255,255,255,0.08);
    width: 100%;
    max-width: 100%;
    font-size: 16px;
    box-sizing: border-box;
    background: rgba(255,255,255,0.02);
    color: #fff;
    &:focus { border-color: #2ecc71; box-shadow: 0 6px 18px rgba(46,204,113,0.06); outline:none; }
`;

const ErrorText = styled.p`
    color: #ff6b6b;
    margin: 6px 0 0 0;
    font-size: 14px;
    text-align: left;
`;

const TimeLeftText = styled.span`
    color: #ff6b6b;
    font-size: 14px;
    display: block;
    margin-top: 4px;
    text-align: left;
`;

const ModalButtons = styled.div`
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    margin-top: 18px;
`;

const ModalButton = styled.button`
    background: ${props => props.variant === 'secondary' ? '#2b2f33' : '#22c55e'};
    border: none;
    color: #fff;
    padding: 10px 18px;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 700;
    transition: transform .12s ease, filter .12s ease;
    &:hover { filter: brightness(1.06); transform: translateY(-2px); }
    &:disabled { opacity: 0.5; cursor: not-allowed; transform: none; filter: none; }
`;

export default ZmenaPrezdivky;
